/**
 * Creates a Finder-style stacked-cards drag ghost for multi-select drag operations.
 *
 * Visual: 2 offset shadow cards behind the main card, with a count badge.
 * The ghost is appended to the body off-screen, used as setDragImage, then removed.
 *
 * Returns { element, cleanup } — call cleanup() in a setTimeout(0) after setDragImage.
 */
export function createMultiDragGhost(
  sourceElement: HTMLElement,
  count: number,
  cursorOffset: { x: number; y: number },
): { element: HTMLElement; offsetX: number; offsetY: number } {
  const rect = sourceElement.getBoundingClientRect();
  const scale = 0.8;
  const cardW = rect.width * scale;
  const cardH = rect.height * scale;

  // Stack offsets — cards behind the main one
  const stackOffset = 4;
  const stackCount = Math.min(count, 3); // Show max 3 layers

  // Container needs extra space for the stack and badge
  const extraSpace = stackOffset * (stackCount - 1);
  const badgeOverflow = 12; // badge sticks out by ~12px top & right

  const container = document.createElement('div');
  container.style.cssText = `
    position: absolute;
    top: -9999px;
    left: -9999px;
    width: ${cardW + extraSpace + badgeOverflow}px;
    height: ${cardH + extraSpace + badgeOverflow}px;
    pointer-events: none;
  `;

  // Create stacked shadow cards (back to front)
  for (let i = stackCount - 1; i >= 1; i--) {
    const shadow = document.createElement('div');
    shadow.style.cssText = `
      position: absolute;
      top: ${badgeOverflow + (i * stackOffset)}px;
      left: ${i * stackOffset}px;
      width: ${cardW}px;
      height: ${cardH}px;
      border-radius: 8px;
      background: var(--bg-secondary, #f0f1f3);
      border: 1px solid var(--border-color, #d1d5db);
      opacity: ${0.6 - (i * 0.15)};
    `;
    container.appendChild(shadow);
  }

  // Main card — clone of the actual element
  const clone = sourceElement.cloneNode(true) as HTMLElement;
  clone.style.cssText = `
    position: absolute;
    top: ${badgeOverflow}px;
    left: 0px;
    width: ${cardW}px;
    height: ${cardH}px;
    opacity: 0.85;
    background: var(--bg-secondary, #f9fafb);
    border: 1px solid var(--border-color, #d1d5db);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  `;
  // Remove the checkbox from the clone
  const checkbox = clone.querySelector('.item-checkbox');
  if (checkbox) checkbox.remove();
  // Remove action buttons from the clone
  const actions = clone.querySelector('.file-actions, .folder-actions');
  if (actions) actions.remove();
  // Remove meta info (file size, folder/file counts)
  const meta = clone.querySelector('.file-meta, .folder-meta');
  if (meta) meta.remove();
  // Remove name label
  const name = clone.querySelector('.file-name, .folder-name');
  if (name) name.remove();
  container.appendChild(clone);

  // Count badge — prominent, top-right, blue background
  const badge = document.createElement('div');
  badge.textContent = String(count);
  badge.style.cssText = `
    position: absolute;
    top: 0px;
    right: 0px;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    border-radius: 12px;
    background: #007aff;
    color: white;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0, 122, 255, 0.4);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  `;
  container.appendChild(badge);

  document.body.appendChild(container);
  // Force reflow so the browser captures styles
  container.offsetHeight;

  const offsetX = cursorOffset.x * scale;
  const offsetY = cursorOffset.y * scale + badgeOverflow;

  return { element: container, offsetX, offsetY };
}
